import React, { FC, Fragment, PropsWithChildren, ReactNode, useEffect, useState } from 'react';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { InferProps } from 'prop-types';
import Button, { ButtonSize } from '../Button/Button';
import classNames from 'classnames';
import { usePathname } from 'next/navigation';
import Icon from '@/src/app/components/Icon/Icon';
import { IconName } from '@/src/app/components/Icon/types';
import { Dictionary } from '@/src/lib/dictionary/types';
import LogoClaim from '@/src/app/components/Logo/LogoClaim';

export interface ModalProps {
  show?: boolean;
  title?: string;
  description?: string;
  accept?: string;
  decline?: string;
  size?: 'large' | 'medium' | 'small';
  open?: boolean;
  simple?: boolean;
  onAcceptHandler?: () => unknown;
  onDeclineHandler?: () => unknown;
  onCloseHandler?: () => unknown;
  onModalClose?: () => unknown;
  children?: ReactNode;
  dict: Dictionary;
  lang: string;
  country: string;
}

const Modal: FC<ModalProps> = ({
  title,
  description,
  accept,
  decline,
  children,
  dict,
  size = 'small',
  open = true,
  simple = false,
  onAcceptHandler = () => null,
  onDeclineHandler = () => null,
  onCloseHandler = () => null,
  onModalClose = () => null,
  lang,
  country,
}: InferProps<PropsWithChildren<ModalProps>>) => {
  const [isOpened, setIsOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const pathname = usePathname();
  const isDisplayHeaderEnabled = !simple && size === 'large';

  useEffect(() => {
    if (loading || !isOpened) {
      return;
    }

    setIsOpened(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    setIsOpened(true);
  }, []);

  useEffect(() => {
    setIsOpened(open);
  }, [open]);

  function delayedClose() {
    setTimeout(() => {
      onModalClose();
    }, 300);
  }

  const wrapHandler = (handler: () => unknown) => {
    return async () => {
      setError(null);
      setLoading(true);
      try {
        await handler();
        setLoading(false);
        setIsOpened(false);
        delayedClose();
      } catch (_) {
        setLoading(false);
        setError(dict.general.error);
      }
    };
  };

  const wrapCloseHandler = (handler: () => unknown) => {
    return async () => {
      if (loading) {
        return;
      } else {
        await handler();
        setIsOpened(false);
        delayedClose();
      }
    };
  };

  return (
    <Dialog open={isOpened} className="relative z-40 " onClose={() => wrapCloseHandler(onCloseHandler)}>
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-500/75 duration-300 ease-out data-[closed]:opacity-0"
      />

      <div className="fixed inset-0 z-10 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
          <DialogPanel
            transition
            className={classNames(
              'relative transform bg-white text-left shadow-xl  sm:my-8 sm:w-full modal-content transition-all duration-300 ease-out data-[closed]:opacity-0 data-[closed]:sm:scale-95',
              {
                'sm:!max-w-lg': size === 'small',
                'sm:!max-w-[606px]': size === 'medium',
                'max-w-4xl!': size === 'large',
              },
            )}
          >
            {isDisplayHeaderEnabled ? (
              <div className="bg-primary py-6 flex flex-row-reverse">
                <LogoClaim className="w-[274px]! h-auto!" lang={lang} country={country} />
              </div>
            ) : null}
            <div
              className={classNames('relative p-[1.875rem]', {
                'lg:p-12': size === 'medium',
                'lg:p-8': size === 'large',
              })}
              data-testid="modal"
            >
              <div className="absolute top-0 right-0 pt-6 pr-6 block z-20">
                <button
                  type="button"
                  className="bg-white text-gray-400 pl-1 hover:text-gray-500 focus:outline-none"
                  disabled={loading}
                  onClick={wrapHandler(onCloseHandler)}
                  data-testid="modal-close"
                >
                  <span className="sr-only">{dict.general.close}</span>
                  <Icon
                    name={IconName.CLOSE}
                    className="text-4xl!"
                    style={{ fontVariationSettings: '"FILL" 0, "wght" 200, "GRAD" 0, "opsz" 24' }}
                  />
                </button>
              </div>
              {children ? (
                children
              ) : (
                <>
                  <div className="sm:flex sm:items-start">
                    <div>
                      {title && (
                        <Dialog.Title as="h3" className="display-title-3xl pr-6 mb-5">
                          {title}
                        </Dialog.Title>
                      )}
                      {error && (
                        <div className="mt-2">
                          <p className="text-sm text-red-600 text-bold">{error}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-base text-gray-500">{description}</p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 flex justify-between">
                    <Button
                      primary
                      outline
                      disabled={loading}
                      size={ButtonSize.MEDIUM}
                      onClick={wrapHandler(onDeclineHandler)}
                    >
                      {decline}
                    </Button>
                    <Button
                      primary
                      priority
                      loading={loading}
                      size={ButtonSize.MEDIUM}
                      onClick={wrapHandler(onAcceptHandler)}
                    >
                      {accept}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default Modal;
